import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { TodoCard } from "../../components/TodoCard";
import { Link } from "wouter";
import { collection, collectionGroup, doc, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { ChevronDown, ChevronUp, EyeOff, GripVertical, Plus, SlidersHorizontal, Star } from "lucide-react";
import { paths, ROLE_RANK, SECTION_BY_ID, type CompanyDoc, type Invoice, type Job, type TimeEntry } from "@clevis/shared-types";
import { Button, Card, CardTitle, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { useAuth } from "../../hooks/useAuth";
import { useAccess } from "../../hooks/useAccess";
import { useUiPrefs } from "../../hooks/useUiPrefs";
import { useMemberNames } from "../../hooks/useMemberNames";
import { DepartmentFilter, useDepartments } from "../../hooks/useDepartments";
import { announceHash } from "../../components/SidebarNav";
import { BUCKET_LABEL, computePipeline, type Bucket } from "../../lib/pipeline";
import { expiryState, fmtExpiry, todayUtcMidnight } from "../../components/SendDocuments";
import { LienNoticesDue, TwoStepNotice } from "../../components/TwoStepGate";

// Exception-first dashboard, built from widgets each person arranges: show,
// hide, drag (or arrow) into order. Defaults depend on the role; a widget only
// exists for someone who can see its data. Widgets show what needs attention,
// never rosters of zeros.

const money = (c: number) => `$${(c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (ms: number | null) => (ms ? new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "");
const DAY = 86400000;
type JobRow = Job & { id: string };

interface WidgetDef {
  id: string;
  title: string;
  wide: boolean;
  /** Who can have it: the sections whose data it shows. */
  needs: string[];
  managerOnly?: boolean;
}
const WIDGETS: WidgetDef[] = [
  { id: "pinned", title: "Pinned pipeline tiles", wide: true, needs: ["pipeline"], managerOnly: true },
  { id: "attention", title: "Needs attention", wide: true, needs: ["dashboard"] },
  { id: "todos", title: "To-dos", wide: true, needs: ["dashboard"] },
  { id: "today", title: "Today's jobs", wide: true, needs: ["jobs"] },
  { id: "favorites", title: "Favorites", wide: true, needs: ["dashboard"] },
  { id: "unassigned", title: "No tech assigned", wide: false, needs: ["jobs"] },
  { id: "week", title: "Next 7 days", wide: false, needs: ["jobs"] },
  { id: "newjobs", title: "Recently added jobs", wide: false, needs: ["jobs"] },
  { id: "clock", title: "On the clock now", wide: false, needs: ["timesheets"], managerOnly: true },
  { id: "money", title: "Money out there", wide: false, needs: ["invoices"], managerOnly: true },
];
const DEFAULT_ORDER: Record<"office" | "manager", string[]> = {
  office: ["attention", "todos", "today", "favorites", "unassigned", "week", "newjobs"],
  manager: ["pinned", "attention", "todos", "today", "unassigned", "clock", "money", "week", "newjobs", "favorites"],
};

export function Dashboard() {
  const { tenantId, role, user } = useAuth();
  const can = useAccess();
  const { prefs, loaded, save } = useUiPrefs();
  const isManager = role ? ROLE_RANK[role] >= 2 : false;
  const [editing, setEditing] = useState(false);
  const dragId = useRef<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  // Which widgets this person may have, in their order, minus the hidden ones.
  const available = WIDGETS.filter((w) => w.needs.every((s) => can(s)) && (!w.managerOnly || isManager));
  const availableIds = new Set(available.map((w) => w.id));
  const defaults = DEFAULT_ORDER[isManager ? "manager" : "office"];
  const savedOrder = prefs.dashboard?.order?.length ? prefs.dashboard.order : defaults;
  const order = [...savedOrder.filter((id) => availableIds.has(id)), ...available.map((w) => w.id).filter((id) => !savedOrder.includes(id))];
  const hidden = new Set(prefs.dashboard?.hidden ?? (prefs.dashboard ? [] : available.map((w) => w.id).filter((id) => !defaults.includes(id))));
  const shown = order.filter((id) => !hidden.has(id));
  const on = (id: string) => shown.includes(id);

  const saveLayout = (nextOrder: string[], nextHidden: Set<string>) => save({ dashboard: { order: nextOrder, hidden: [...nextHidden] } });
  const move = (id: string, to: number) => {
    const list = [...order];
    const from = list.indexOf(id);
    if (from < 0 || to < 0 || to >= list.length) return;
    list.splice(to, 0, list.splice(from, 1)[0]!);
    saveLayout(list, hidden);
  };
  const hide = (id: string) => saveLayout(order, new Set([...hidden, id]));
  const unhide = (id: string) => saveLayout(order, new Set([...hidden].filter((h) => h !== id)));

  // ---------- data, loaded only for widgets on screen ----------
  const [allTodayJobs, setTodayJobs] = useState<JobRow[]>([]);
  const [allOpenJobs, setOpenJobs] = useState<JobRow[]>([]);
  const [allNewJobs, setNewJobs] = useState<JobRow[]>([]);
  // Job widgets follow the person's department (remembered; same as Jobs and Schedule).
  const dept = useDepartments();
  const todayJobs = allTodayJobs.filter((j) => dept.jobMatches(j));
  const openJobs = allOpenJobs.filter((j) => dept.jobMatches(j));
  const newJobs = allNewJobs.filter((j) => dept.jobMatches(j));
  const [clockedIn, setClockedIn] = useState<Array<TimeEntry & { id: string }>>([]);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [openInvoices, setOpenInvoices] = useState<Array<Invoice & { id: string }>>([]);
  const [newRequests, setNewRequests] = useState(0);
  const [readyParts, setReadyParts] = useState(0);
  const [lowStock, setLowStock] = useState(0);
  const [expiringDocs, setExpiringDocs] = useState<Array<{ id: string; name: string; expiresAt: number }>>([]);
  const [pins, setPins] = useState<Bucket[]>([]);
  const [allJobs, setAllJobs] = useState<JobRow[]>([]);
  const [allInvoices, setAllInvoices] = useState<Array<Invoice & { id: string }>>([]);
  const [openParts, setOpenParts] = useState<Set<string>>(new Set());
  const memberName = useMemberNames(tenantId);
  const dayStart = useMemo(() => new Date().setHours(0, 0, 0, 0), []);
  const needOpen = on("unassigned") || on("week");
  const wantAttention = on("attention");

  useEffect(() => {
    if (!tenantId || !on("today")) return;
    return onSnapshot(
      query(collection(db, paths.tenantJobs(tenantId)), where("startAt", ">=", dayStart), where("startAt", "<", dayStart + DAY), orderBy("startAt")),
      (snap) => setTodayJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, dayStart, on("today")]);

  useEffect(() => {
    if (!tenantId || !needOpen) return;
    return onSnapshot(query(collection(db, paths.tenantJobs(tenantId)), where("status", "in", ["scheduled", "in_progress"])), (snap) =>
      setOpenJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
    );
  }, [tenantId, needOpen]);

  useEffect(() => {
    if (!tenantId || !on("newjobs")) return;
    return onSnapshot(
      query(collection(db, paths.tenantJobs(tenantId)), where("createdAt", ">=", Date.now() - 7 * DAY), orderBy("createdAt", "desc"), limit(8)),
      (snap) => setNewJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, on("newjobs")]);

  useEffect(() => {
    if (!tenantId || !isManager) return;
    const subs: Array<() => void> = [];
    if ((on("clock") || wantAttention) && can("timesheets")) {
      subs.push(
        onSnapshot(query(collection(db, paths.tenantTimeEntries(tenantId)), where("status", "==", "open")), (snap) =>
          setClockedIn(snap.docs.map((d) => ({ id: d.id, ...(d.data() as TimeEntry) }))),
        ),
        onSnapshot(query(collection(db, paths.tenantTimeEntries(tenantId)), where("status", "==", "submitted")), (snap) => setPendingApprovals(snap.size)),
      );
    }
    if ((on("money") || wantAttention) && can("invoices")) {
      subs.push(
        onSnapshot(query(collection(db, paths.tenantInvoices(tenantId)), where("status", "in", ["sent", "partially_paid"])), (snap) =>
          setOpenInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) }))),
        ),
      );
    }
    if (wantAttention && can("requests")) {
      subs.push(onSnapshot(query(collection(db, paths.tenantRequests(tenantId)), where("status", "==", "new")), (snap) => setNewRequests(snap.size)));
    }
    if (wantAttention && can("pipeline")) {
      subs.push(
        onSnapshot(query(collectionGroup(db, "orderedItems"), where("tenantId", "==", tenantId), where("status", "==", "ready")), (snap) =>
          setReadyParts(snap.size),
        ),
      );
    }
    if (wantAttention && can("inventory")) {
      subs.push(
        onSnapshot(query(collection(db, paths.tenantPricebook(tenantId)), where("trackInventory", "==", true)), (snap) =>
          setLowStock(
            snap.docs.filter((d) => {
              const r = d.get("reorderLevel") as number | null;
              return r != null && ((d.get("onHandQty") as number | undefined) ?? 0) <= r;
            }).length,
          ),
        ),
      );
    }
    return () => subs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, isManager, wantAttention, on("clock"), on("money"), can]);

  // Company documents that expired or expire within 30 days, for whoever keeps the library.
  useEffect(() => {
    if (!tenantId || !wantAttention || !can("documents")) return;
    return onSnapshot(collection(db, paths.tenantCompanyDocs(tenantId)), (snap) =>
      setExpiringDocs(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as CompanyDoc) }))
          .filter((d) => !d.archivedAt && expiryState(d.expiresAt) !== null)
          .map((d) => ({ id: d.id, name: d.name, expiresAt: d.expiresAt! }))
          .sort((a, b) => a.expiresAt - b.expiresAt),
      ),
    );
  }, [tenantId, wantAttention, can]);

  // Pinned pipeline tiles (picked on the Pipeline page); full lists load only when pinned.
  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, paths.user(user.uid)), (snap) => setPins(((snap.get("pinnedPipelineTiles") as string[] | undefined) ?? []) as Bucket[]));
  }, [user]);
  useEffect(() => {
    if (!tenantId || pins.length === 0 || !on("pinned")) return;
    const subs = [
      onSnapshot(collection(db, paths.tenantJobs(tenantId)), (snap) => setAllJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) })))),
      onSnapshot(collection(db, paths.tenantInvoices(tenantId)), (snap) => setAllInvoices(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Invoice) })))),
      onSnapshot(query(collectionGroup(db, "orderedItems"), where("tenantId", "==", tenantId), where("status", "==", "ordered")), (snap) =>
        setOpenParts(new Set(snap.docs.map((d) => (d.get("jobId") as string) ?? ""))),
      ),
    ];
    return () => subs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, pins.length > 0, on("pinned")]);

  // ---------- derived ----------
  const myJobs = todayJobs.filter((j) => user && j.assignedUids.includes(user.uid));
  const todayList = myJobs.length > 0 ? myJobs : todayJobs;
  const unassignedToday = todayJobs.filter((j) => j.assignedUids.length === 0 && j.status === "scheduled");
  const unassigned = openJobs.filter((j) => j.assignedUids.length === 0 && j.status === "scheduled");
  const unassignedSoon = unassigned.filter((j) => j.startAt && j.startAt >= dayStart).sort((a, b) => a.startAt! - b.startAt!);
  const overdue = openInvoices.filter((i) => i.dueAt !== null && i.dueAt < Date.now());
  const outstandingCents = openInvoices.reduce((s, i) => s + (i.totalCents - i.amountPaidCents), 0);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const start = dayStart + i * DAY;
    return { start, jobs: openJobs.filter((j) => j.startAt && j.startAt >= start && j.startAt < start + DAY) };
  });
  const pinnedCounts =
    pins.length > 0 && allJobs.length > 0
      ? (() => {
          const buckets = computePipeline(allJobs, allInvoices, openParts);
          return pins.map((b) => ({ bucket: b, count: buckets.get(b)?.length ?? 0 }));
        })()
      : [];

  const attention: Array<{ text: string; href: string }> = [];
  if (newRequests > 0) attention.push({ text: `${newRequests} new online request${newRequests === 1 ? "" : "s"} waiting`, href: "/requests" });
  if (unassignedToday.length > 0) attention.push({ text: `${unassignedToday.length} of today's jobs have no tech assigned`, href: "/schedule" });
  if (pendingApprovals > 0) attention.push({ text: `${pendingApprovals} timesheet${pendingApprovals === 1 ? "" : "s"} waiting for approval`, href: "/timesheets" });
  if (readyParts > 0) attention.push({ text: `${readyParts} part${readyParts === 1 ? "" : "s"} ready to order`, href: "/pipeline" });
  if (lowStock > 0) attention.push({ text: `${lowStock} inventory item${lowStock === 1 ? "" : "s"} below reorder level`, href: "/inventory" });
  if (overdue.length > 0)
    attention.push({
      text: `${overdue.length} overdue invoice${overdue.length === 1 ? "" : "s"} (${money(overdue.reduce((s, i) => s + (i.totalCents - i.amountPaidCents), 0))})`,
      href: "/invoices",
    });
  const today = todayUtcMidnight();
  for (const d of expiringDocs.slice(0, 3)) {
    const days = Math.round((d.expiresAt - today) / DAY);
    attention.push({
      text:
        days < 0
          ? `${d.name} expired ${fmtExpiry(d.expiresAt)}. Upload the renewal`
          : `${d.name} expires ${days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${days} days`} (${fmtExpiry(d.expiresAt)})`,
      href: "/documents",
    });
  }
  if (expiringDocs.length > 3) attention.push({ text: `${expiringDocs.length - 3} more company documents expire soon`, href: "/documents" });
  const favorites = (prefs.favorites ?? []).filter((id) => can(id) && SECTION_BY_ID.has(id));

  const jobLine = (j: JobRow, right: ReactNode) => (
    <Link key={j.id} href={`/jobs/${j.id}`} className="flex items-center justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0 hover:bg-cream/60">
      <span className="min-w-0 truncate text-ink">
        {j.customerName ? <strong>{j.customerName} · </strong> : null}
        {j.title}
      </span>
      <span className="shrink-0 text-xs text-steel">{right}</span>
    </Link>
  );

  // ---------- widgets ----------
  function render(id: string): ReactNode | null {
    switch (id) {
      case "pinned":
        if (pinnedCounts.length === 0)
          return editing ? (
            <Card>
              <p className="text-xs text-steel">Pin pipeline tiles on the Pipeline page and they show here.</p>
            </Card>
          ) : null;
        return (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {pinnedCounts.map(({ bucket, count }) => (
              <Link key={bucket} href="/pipeline" className="rounded-xl border border-steel/25 bg-white px-3 py-2 transition-colors hover:border-brandorange/60">
                <div className={cn("text-xl font-bold", count > 0 ? "text-ink" : "text-steel/60")}>{count}</div>
                <div className="text-[11px] leading-tight text-steel">{BUCKET_LABEL[bucket] ?? bucket}</div>
              </Link>
            ))}
          </div>
        );
      case "attention":
        if (attention.length === 0)
          return editing ? (
            <Card>
              <p className="text-xs text-steel">Nothing needs attention right now. This only shows up when something does.</p>
            </Card>
          ) : null;
        return (
          <Card className="border-brandorange/50">
            <CardTitle className="text-brandorange-dark">Needs attention</CardTitle>
            <div className="space-y-1">
              {attention.map((a, i) => (
                <Link key={i} href={a.href} className="block text-sm text-ink hover:text-brandorange">
                  → {a.text}
                </Link>
              ))}
            </div>
          </Card>
        );
      case "today":
        return (
          <Card className="p-0">
            <div className="flex items-center justify-between px-4 pt-3">
              <CardTitle className="mb-0">{myJobs.length > 0 ? `Your jobs today (${myJobs.length})` : `Today's jobs (${todayJobs.length})`}</CardTitle>
              {can("schedule") ? (
                <Link href="/schedule" className="text-xs text-steel hover:text-ink">
                  Open schedule →
                </Link>
              ) : null}
            </div>
            <div className="mt-1">
              {todayList.slice(0, 8).map((j) => jobLine(j, <>{fmtTime(j.startAt)} · {j.status.replace("_", " ")}</>))}
            </div>
            {todayJobs.length === 0 ? <div className="px-4 py-6 text-center text-sm text-steel">Nothing on the schedule today.</div> : null}
          </Card>
        );
      case "favorites":
        return (
          <Card>
            <CardTitle>Favorites</CardTitle>
            {favorites.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {favorites.map((id) => {
                  const s = SECTION_BY_ID.get(id)!;
                  return (
                    <Link
                      key={id}
                      href={s.href}
                      onClick={() => announceHash(s.href)}
                      className="flex items-center gap-1 rounded-lg border border-steel/25 px-2.5 py-1 text-sm text-ink hover:border-brandorange"
                    >
                      <Star size={12} className="text-brandorange" fill="currentColor" />
                      {s.label}
                    </Link>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-steel">Star items in the menu (Customize menu, bottom of the sidebar) and they show here too.</p>
            )}
          </Card>
        );
      case "unassigned":
        return (
          <Card className="h-full p-0">
            <div className="flex items-center justify-between px-4 pt-3">
              <CardTitle className="mb-0">No tech assigned ({unassigned.length})</CardTitle>
              <Link href="/jobs" className="text-xs text-steel hover:text-ink">
                Jobs →
              </Link>
            </div>
            <div className="mt-1">
              {unassignedSoon.slice(0, 5).map((j) => jobLine(j, new Date(j.startAt!).toLocaleDateString(undefined, { month: "short", day: "numeric" })))}
            </div>
            {unassigned.length === 0 ? <p className="px-4 py-4 text-sm text-steel">Every open job has a tech.</p> : null}
            {unassigned.length > 0 && unassignedSoon.length === 0 ? (
              <p className="px-4 py-3 text-xs text-steel">None coming up; {unassigned.length} have no date or are in the past.</p>
            ) : null}
          </Card>
        );
      case "week":
        return (
          <Card className="h-full">
            <CardTitle>Next 7 days</CardTitle>
            <div className="space-y-0.5">
              {weekDays.map(({ start, jobs }) => (
                <div key={start} className="flex items-center justify-between text-sm">
                  <span className={cn(start === dayStart ? "font-semibold text-ink" : "text-ink")}>
                    {start === dayStart ? "Today" : new Date(start).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  </span>
                  <span className="text-xs text-steel">
                    {jobs.length} job{jobs.length === 1 ? "" : "s"}
                    {jobs.some((j) => j.assignedUids.length === 0) ? (
                      <span className="text-brandorange-dark"> · {jobs.filter((j) => j.assignedUids.length === 0).length} unassigned</span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        );
      case "newjobs":
        return (
          <Card className="h-full p-0">
            <CardTitle className="px-4 pt-3">Recently added</CardTitle>
            {newJobs.map((j) => jobLine(j, new Date(j.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })))}
            {newJobs.length === 0 ? <p className="px-4 pb-3 text-sm text-steel">No new jobs this week.</p> : null}
          </Card>
        );
      case "clock":
        return (
          <Link href="/timesheets" className="block h-full">
            <Card className="h-full transition-colors hover:border-brandorange/60">
              <CardTitle>On the clock now ({clockedIn.length})</CardTitle>
              {clockedIn.length > 0 ? (
                <div className="space-y-1 text-sm text-ink">
                  {clockedIn.slice(0, 8).map((e) => (
                    <div key={e.id} className="flex justify-between">
                      <span className="truncate">{e.displayName || memberName(e.uid)}</span>
                      <span className="text-xs text-steel">since {fmtTime(e.clockInAt)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-steel">Nobody is clocked in.</p>
              )}
            </Card>
          </Link>
        );
      case "money":
        return (
          <Link href="/invoices" className="block h-full">
            <Card className="h-full transition-colors hover:border-brandorange/60">
              <CardTitle>Money out there</CardTitle>
              <p className="text-2xl font-bold text-ink">{money(outstandingCents)}</p>
              <p className="text-xs text-steel">
                across {openInvoices.length} open invoice{openInvoices.length === 1 ? "" : "s"}
                {overdue.length > 0 ? ` · ${overdue.length} overdue` : ""}
              </p>
            </Card>
          </Link>
        );
      case "todos":
        return tenantId ? <TodoCard tenantId={tenantId} /> : null;
      default:
        return null;
    }
  }

  const widget = (id: string) => WIDGETS.find((w) => w.id === id)!;
  const dropProps = (id: string) =>
    editing
      ? {
          draggable: true,
          onDragStart: (e: DragEvent) => {
            dragId.current = id;
            e.dataTransfer.effectAllowed = "move";
          },
          onDragEnd: () => {
            dragId.current = null;
            setOver(null);
          },
          onDragOver: (e: DragEvent) => {
            if (!dragId.current) return;
            e.preventDefault();
            setOver(id);
          },
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            setOver(null);
            const from = dragId.current;
            if (from && from !== id) move(from, order.indexOf(id));
          },
        }
      : {};

  if (!loaded) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <TwoStepNotice />
      <LienNoticesDue />
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-bold">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</h1>
        <div className="flex items-center gap-2">
        <DepartmentFilter dept={dept} className="w-auto py-1 text-xs" />
        {editing ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={() => saveLayout(defaults, new Set(available.map((w) => w.id).filter((x) => !defaults.includes(x))))}
            >
              Reset
            </Button>
            <Button className="px-3 py-1 text-xs" onClick={() => setEditing(false)}>
              Done
            </Button>
          </div>
        ) : (
          <button className="flex items-center gap-1 text-xs text-steel hover:text-ink" onClick={() => setEditing(true)}>
            <SlidersHorizontal size={12} /> Customize
          </button>
        )}
        </div>
      </div>

      {editing ? (
        <p className="text-xs text-steel">Drag widgets (or use the arrows) to reorder them; hide what you don't need, and add it back below.</p>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        {shown.map((id, i) => {
          const body = render(id);
          if (!body && !editing) return null;
          const w = widget(id);
          return (
            <div
              key={id}
              className={cn(w.wide ? "md:col-span-2" : "", editing && "rounded-xl border border-dashed border-steel/40 p-1", over === id && "ring-2 ring-brandorange")}
              {...dropProps(id)}
            >
              {editing ? (
                <div className="mb-1 flex items-center gap-1 px-1 text-xs text-steel">
                  <GripVertical size={13} className="cursor-grab" />
                  <span className="flex-1 font-semibold text-ink">{w.title}</span>
                  <button aria-label={`Move ${w.title} up`} className="hover:text-ink" onClick={() => move(id, i - 1)}>
                    <ChevronUp size={14} />
                  </button>
                  <button aria-label={`Move ${w.title} down`} className="hover:text-ink" onClick={() => move(id, i + 1)}>
                    <ChevronDown size={14} />
                  </button>
                  <button aria-label={`Hide ${w.title}`} className="ml-1 flex items-center gap-0.5 hover:text-ink" onClick={() => hide(id)}>
                    <EyeOff size={13} /> Hide
                  </button>
                </div>
              ) : null}
              {body}
            </div>
          );
        })}
      </div>

      {editing && order.some((id) => hidden.has(id)) ? (
        <Card>
          <CardTitle>Hidden widgets</CardTitle>
          <div className="flex flex-wrap gap-1.5">
            {order
              .filter((id) => hidden.has(id))
              .map((id) => (
                <button key={id} className="flex items-center gap-1 rounded-lg border border-steel/30 px-2.5 py-1 text-sm text-ink hover:border-brandorange" onClick={() => unhide(id)}>
                  <Plus size={12} /> {widget(id).title}
                </button>
              ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
